import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Button, Card, Descriptions, Flex, Input, Typography } from 'antd';

import { capitalize } from '../../../helpers/common';
import { hasPermission } from '../../../helpers/user';

import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';

import { fetchGateway, updateGateway } from '../../../redux/actions/gateway';

import AppTable from '../../../components/AppTable';
import CancelSaveButtons from '../../../components/CancelSaveButtons';
import PrevNextButtons from '../../../components/PrevNextButtons';
import StatusIndicator from '../../../components/StatusIndicator';
import { errorToastHandler } from '../../../components/action_notifier';
import AppList from '../../../components/ui/AppList';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';
import AppDropdown from '../../ui-components/AppDropdown';

import GatewayHistory from './GatewayHistory';

const { Title, Text } = Typography;

function Gateway() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();

  const { gateway, loading } = useSelector(state => state.gateway);
  const { permissions } = useSelector(state => state.user);

  const [state, setState] = useState({
    delete_modal: false,
    editData: {},
    editing: false,
    selected_geofences: []
  });

  useEffect(() => {
    getGateway();
  }, []);

  async function getGateway() {
    try {
      await dispatch(fetchGateway(id)).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  const onCancelEdit = (value, field) => {
    setState(prev => ({ ...prev, editData: gateway, [field]: value }));
  };

  function enableEditing() {
    setState(prev => ({ ...prev, editing: true, editData: { ...gateway } }));
  }

  const onChange = (value, field) => {
    setState(prev => ({ ...prev, editData: { ...state.editData, [field]: value } }));
  };

  async function saveGateway() {
    const { id, name } = state.editData;

    try {
      await dispatch(updateGateway({ id, data: { name } }));

      setState(prev => ({ ...prev, editing: false }));
    } catch (error) {
      errorToastHandler(messages.DEFAULT_ERROR);
    }
  }

  const actions = [
    {
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!hasPermission([PERMISSIONS.GATEWAYS.UPDATE_OWN], permissions)}
          onClick={enableEditing}>
          Edit Kraal Gateway
        </Button>
      )
    }
  ];

  const columns = [
    {
      title: 'Kraal tag',
      dataIndex: 'identifier',
      render: (value, record) => <Link to={`/tag/${record.id}`}>{record.identifier}</Link>
    }
  ];

  const data = [
    {
      label: 'Gateway ID',
      value: gateway?.identifier || '/',
      isVisible: true
    },
    {
      label: 'Gateway Name',
      value: (
        <>
          {!state.editing && <span>{gateway?.name || '/'}</span>}
          {state.editing && (
            <Input name="name" value={state.editData.name} onChange={e => onChange(e.target.value, e.target.name)} />
          )}
        </>
      ),
      isVisible: true
    },
    {
      label: 'Gateway Connection Status',
      value: (
        <div className="d-flex align-items-center">
          <StatusIndicator key={gateway?.id} status={gateway?.status} />
          {capitalize(gateway?.status)}
        </div>
      ),
      isVisible: true
    },
    {
      label: 'Connected Kraal Tags',
      value: gateway?.tags?.length,
      isVisible: true
    }
  ];

  return (
    <>
      {gateway?.id && (
        <>
          <Flex gap="small" vertical={false} justify="space-between" wrap>
            <Flex vertical={true}>
              <Title level={4}>
                {state.editing && 'Edit '}Gateway {gateway?.name || gateway?.appeui}
              </Title>
              <AppBreadcrumb
                items={[
                  {
                    title: <Link to="/devices?tab=gateways">List of Kraal Gateways</Link>
                  },
                  {
                    title: `${state.editing ? 'Edit ' : ''}Gateway ${gateway?.name || gateway?.appeui}`
                  }
                ]}
              />
            </Flex>
            <Flex gap="small">
              {!state.editing && <AppDropdown label="Actions" items={actions} />}

              {state.editing && (
                <CancelSaveButtons
                  disabled={loading}
                  loading={loading}
                  handleCancel={() => onCancelEdit(false, 'editing')}
                  handleSave={saveGateway}
                />
              )}

              <PrevNextButtons
                nextId={gateway?.next_id}
                prevId={gateway?.prev_id}
                handlePrev={() => history.push(`/gateway/${gateway?.prev_id}`)}
                handleNext={() => history.push(`/gateway/${gateway?.next_id}`)}
              />
            </Flex>
          </Flex>

          <Card title={`Gateway ${gateway?.name || gateway?.appeui}`} className="mb-2">
            <AppList>
              {data?.map(d => (
                d.isVisible && (
                  <Descriptions.Item key={d.label} label={d.label}>
                    {d.value}
                  </Descriptions.Item>
                )
              ))}
            </AppList>
          </Card>

          {gateway?.tags?.length > 0 && (
            <Card title="Connected Kraal Tags" className="mb-2">
              <Text type="secondary">Kraal tags connected to {gateway?.identifier}</Text>
              <AppTable
                tableMaxHeight="270px"
                baseColumns={columns}
                dataSource={gateway?.tags}
                loading={false}
                rowsSelectable={false}
                settings={false}
              />
            </Card>
          )}

          <GatewayHistory gatewayId={id} filter={{ filter: data => data.type === 'gateway' }} />
        </>
      )}
    </>
  );
}

export default Gateway;
